import React, { FC } from 'react';
import clsx from 'clsx';
import {
    Chip,
    Grid,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@maas-components/core';
import LinkWrapper from './LinkWrapper';
import useStyles from '../styles';
import { Icon, Service } from '../../types';

export type MenuItemProps = {
    item: Service;
    favorites: string[];
    favMobileSelectionEnabled: boolean;
    onFavoriteRemove: (item: string) => void;
    onFavoriteCheck: (item: string) => void;
    onRecentCheck: (item: string) => void;
    onClose: (...args: unknown[]) => void;
    getIcon: (iconName: Icon) => JSX.Element;
    onClickMenuItem?: (item: Service) => void;
    mode: 'dark' | 'light';
    RouterLink: React.ElementType;
};

const matchUrl = (menuUrl: string, isExternalLink?: boolean) => {
    const pathname = !isExternalLink ? window.location.pathname : window.location.href;
    return menuUrl === pathname;
};

const MenuItem: FC<MenuItemProps> = ({
    item,
    favorites,
    favMobileSelectionEnabled,
    onFavoriteCheck,
    onFavoriteRemove,
    onRecentCheck,
    onClose,
    getIcon,
    onClickMenuItem,
    mode,
    RouterLink,
}) => {
    const classes = useStyles({ mode });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isFavorite = favorites.find((favorite) => favorite === item.url);
    const showFavButton = !isMobile || favMobileSelectionEnabled;
    const selectedMenu = matchUrl(item.url, item.external) ? classes.selectedMenu : '';
    const activeFavorites = !showFavButton ? classes.removeFavorites : classes.addFavorites;

    const handleClick = () => {
        if (onClickMenuItem) {
            onClickMenuItem(item);
        }

        onRecentCheck(item.name);
        onClose();
    };

    return (
        <ListItem className={clsx(classes.menuItem, classes.menuItemHover)}>
            <ListItemIcon className={activeFavorites}>
                {showFavButton && (
                    <IconButton
                        onClick={() => (isFavorite ? onFavoriteRemove(item.url) : onFavoriteCheck(item.url))}
                        edge="end"
                        aria-label="comments"
                    >
                        {isFavorite ? getIcon('Favorite') : getIcon('FavoriteBorder')}
                    </IconButton>
                )}
            </ListItemIcon>
            <LinkWrapper mode={mode} onClick={handleClick} item={item} RouterLink={RouterLink}>
                <Grid alignItems="center" wrap="nowrap" container>
                    <ListItemIcon className={classes.menuItemIcon}>
                        {item.elementIcon || getIcon(item.icon)}
                    </ListItemIcon>
                    <ListItemText className={selectedMenu} primary={item.name} />
                    {item.isNew && <Chip size="small" label="Novo" className={classes.newChip} />}
                </Grid>
            </LinkWrapper>
        </ListItem>
    );
};

export default MenuItem;
